import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { Messages } from '../../../../codegen/Localize';
import { ISubscriptionInfo } from '../../../../ts/models/ISubscriptionInfo';
import { Set } from '../../../../ts/models/Set';
import { openConfirmActionDialog } from '../../../../ts/services/ReactConfirmActionDialog';
import {
  SubscriptionChangeFormatDialogProps,
  SubscriptionChangeFormatDialogState,
  SubscriptionChangeFormatDialogBody,
  setSubscriptionFormat,
  convertAttachBoolsToSubscriptionFormat,
  shouldButtonBeDisabled as shouldSubscriptionChangeFormatDialogButtonBeDisabled
} from '../../../content/SubscriptionChangeFormatDialog';
import { ChangeScheduleAction } from '../../../../ts/services/ChangeScheduleAction';
import { ChangeSubscriptionDataConditionAction } from '../../../../ts/services/ChangeSubscriptionDataConditionAction';
import { ChangeSubscriptionsFormatAction } from '../../../../ts/services/ChangeSubscriptionsFormatAction';
import { ChangeSubscriptionSubjectAction } from '../../../../ts/services/ChangeSubscriptionSubjectAction';
import { DeleteSubscriptionsAction } from '../../../../ts/services/DeleteSubscriptionsAction';
import { ResumeSubscriptionAction } from '../../../../ts/services/ResumeSubscriptionAction';
import {
  ChangeSubscriptionDataCondition,
  ChangeSubscriptionsFormat,
  ChangeSubscriptionsFrequency,
  ChangeSubscriptionsSchedule,
  ChangeSubscriptionSubject,
  DeleteSubscriptions,
  ResumeSubscription
} from '../../../actions/AppActionTypes';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import {
  ChangeSubscriptionDataConditionAction as ChangeSubscriptionDataConditionReduxAction,
  ChangeSubscriptionsFormatAction as ChangeSubscriptionsFormatReduxAction,
  ChangeSubscriptionsFrequencyAction as ChangeSubscriptionsFrequencyReduxAction,
  ChangeSubscriptionsScheduleAction as ChangeSubscriptionsScheduleReduxAction,
  ChangeSubscriptionSubjectAction as ChangeSubscriptionSubjectReduxAction,
  DeleteSubscriptionsAction as DeleteSubscriptionsReduxAction,
  ResumeSubscriptionAction as ResumeSubscriptionReduxAction
} from '../../../actions/spaces/SubscriptionsActions';
import { ServiceLocator } from '../../../ServiceLocator';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { DialogType } from '@tableau/widgets';
import { dialogBodyStyle as changeFormatDialogBodyStyle} from '../../../content/styles/SubscriptionChangeFormatDialogStyles';


export function* handleChangeSubscriptionSubject(action: ChangeSubscriptionSubjectReduxAction) {
  try {
    yield call(changeSubscriptionSubject, action.subscription);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function changeSubscriptionSubject(subscription: ISubscriptionInfo) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ChangeSubscriptionSubjectAction>('ChangeSubscriptionSubjectAction')
      .changeSubject(subscription, context);
}

export function* handleChangeSubscriptionsSchedule(action: ChangeSubscriptionsScheduleReduxAction) {
  try {
    yield call(changeSubscriptionsSchedule, action.subscriptions);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.changeSubscriptionScheduleFailure()));
  }
}

export function changeSubscriptionsSchedule(subscriptions: ISubscriptionInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ChangeScheduleAction>('ChangeScheduleAction')
      .forSubscriptions(Set.fromIdItemArray(subscriptions), context);
}

export function* handleChangeSubscriptionDataCondition(action: ChangeSubscriptionDataConditionReduxAction) {
  try {
    yield call(changeSubscriptionDataCondition, action.subscription);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.changeSubscriptionDataConditionFailure()));
  }
}

export function changeSubscriptionDataCondition(subscription: ISubscriptionInfo) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ChangeSubscriptionDataConditionAction>('ChangeSubscriptionDataConditionAction')
      .changeDataCondition(subscription, context);
}

export function* handleDeleteSubscriptions(action: DeleteSubscriptionsReduxAction) {
  try {
    yield call(deleteSubscriptions, action.subscriptions);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function deleteSubscriptions(subscriptions: ISubscriptionInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<DeleteSubscriptionsAction>('DeleteSubscriptionsAction')
      .deleteSubscriptions(Set.fromIdItemArray(subscriptions), context);
}

export function* handleResumeSubscription(action: ResumeSubscriptionReduxAction) {
  try {
    yield call(resumeSubscription, action.subscription);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function resumeSubscription(subscription: ISubscriptionInfo) {
  return ServiceLocator.get<ResumeSubscriptionAction>('ResumeSubscriptionAction')
      .resumeSubscription(subscription);
}

export function* handleChangeSubscriptionsFormat(action: ChangeSubscriptionsFormatReduxAction) {
  if (FeatureFlags.pdfSubscriptionOptionsEnabled) {
    try {
      yield call(openChangeSubscriptionsFormatDialog, action.subscriptions);
      yield call(refreshAfterSubscriptionActionSucceeded);
    } catch(e) {
      // The ReactConfirmActionDialog throws an exception when it is closed or dismissed
      // by normal means. As a result, we choose to fail silently here, rather than
      // pop an error toast when the dialog is closed properly
      return;
    }
  } else {
    try {
      yield call(changeSubscriptionsFormat, action.subscriptions);
      yield call(refreshAfterSubscriptionActionSucceeded);
    } catch(e) {
      yield put(requestFailed(e, Messages.changeSubscriptionsFormatFailure()));
    }
  }
}

export function changeSubscriptionsFormat(subscriptions: ISubscriptionInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ChangeSubscriptionsFormatAction>('ChangeSubscriptionsFormatAction')
      .changeFormat(Set.fromIdItemArray(subscriptions), context);
}

export function openChangeSubscriptionsFormatDialog(subscriptions: ISubscriptionInfo[]) {
  const subscription = subscriptions[0];
  const modalInstance = openConfirmActionDialog<SubscriptionChangeFormatDialogProps, SubscriptionChangeFormatDialogState, IResult>({
    title: '',
    bodyComponent: SubscriptionChangeFormatDialogBody,
    initialBodyProps: {
      updateState: () => {},
      subscriptions,
      initialFormat: convertAttachBoolsToSubscriptionFormat(subscription.attachImage, subscription.attachPdf),
      initialPageOrientation: subscription.pageOrientation,
      initialPageSizeOption: subscription.pageSizeOption,
    },
    initialDialogState: {
      format: convertAttachBoolsToSubscriptionFormat(subscription.attachImage, subscription.attachPdf),
      pageOrientation: subscription.pageOrientation,
      pageSizeOption: subscription.pageSizeOption,
    },
    actionName: Messages.confirmChangeSubscriptionsFormat(),
    action: setSubscriptionFormat,
    actionDisabled: shouldSubscriptionChangeFormatDialogButtonBeDisabled,
    dialogType: DialogType.Uber,
    dialogBodyStyle: changeFormatDialogBodyStyle,
  });

  return modalInstance.result;
}

export function* handleChangeSubscriptionsFrequency(action: ChangeSubscriptionsFrequencyReduxAction) {
  try {
    yield call(changeSubscriptionsFrequency, action.subscriptions);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.changeSubscriptionsFrequencyFailure()));
  }
}

export function changeSubscriptionsFrequency(subscriptions: ISubscriptionInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ServiceLocator.get<ChangeScheduleAction>('ChangeScheduleAction')
    .changeSubscriptionsFrequency(Set.fromIdItemArray(subscriptions), context);
}

export function* refreshAfterSubscriptionActionSucceeded() {
  yield put(requestSpaceContents());
}

export function* rootSubscriptionSagas(): SagaIterator {
  yield takeEvery(ChangeSubscriptionSubject, handleChangeSubscriptionSubject);
  yield takeEvery(ChangeSubscriptionsSchedule, handleChangeSubscriptionsSchedule);
  yield takeEvery(ChangeSubscriptionDataCondition, handleChangeSubscriptionDataCondition);
  yield takeEvery(DeleteSubscriptions, handleDeleteSubscriptions);
  yield takeEvery(ResumeSubscription, handleResumeSubscription);
  yield takeEvery(ChangeSubscriptionsFormat, handleChangeSubscriptionsFormat);
  yield takeEvery(ChangeSubscriptionsFrequency, handleChangeSubscriptionsFrequency);
}
